import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Loader2,
  XCircle,
  SkipForward,
  Ban,
  Download,
} from 'lucide-react';
import {
  bulkImportStudents,
  fetchActiveCampuses,
  type BulkImportResponse,
  type BulkImportOptions,
  type CampusOption,
} from '@/services/userService';
import { getAcademicYears, type FilterOption } from '@/services/academicManagementService';

interface BulkImportStudentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ImportStep = 'upload' | 'options' | 'importing' | 'results';

// Template headers matching ministry nomenclature
const TEMPLATE_HEADERS = [
  'N° Massar/CNE',
  'N° CNIE',
  'N° passeport',
  'Nom',
  'Prénom',
  'Email',
  'Sexe',
  'Date de naissance',
  'Nationalité',
  'Sigle université',
  'Université',
  'Sigle établissement',
  'Etablissement',
  'Diplôme',
  'Filière',
  'Code filière',
  'Niveau de formation',
  'Type de formation',
  'Situation',
  'Diplôme d\'accès',
  'Série du baccalauréat',
  'Année d\'obtention du baccalauréat',
  'Spécialité du diplôme d\'accès',
  'Mention du Diplôme d\'accès',
  'Lieu d\'obtention du Diplôme d\'accès',
  'Code Lieu d\'obtention du Diplôme d\'accès',
  '1ère année d\'inscription à l\'établissement',
  'Handicapé',
  'Résident',
  'Fonctionnaire',
  'Pourcentage de la bourse',
  'Mobilité',
  'Documents fournis',
  'Session',
  'Statut',
  'Motif de la décision',
];

// Example row for the template
const TEMPLATE_EXAMPLE_ROW = [
  'R123456789', // N° Massar/CNE
  'AB123456', // N° CNIE
  '', // N° passeport
  'ALAMI', // Nom
  'Mohammed', // Prénom
  'mohammed.alami@example.com', // Email
  'm', // Sexe (m/f)
  '15/01/2000', // Date de naissance
  'Maroc', // Nationalité
  'ENRPU-prive', // Sigle université
  'Etablissement privés ne relevant pas des universités', // Université
  'EDGE-Casablanca', // Sigle établissement
  'EDGE Private Business School Casablanca', // Etablissement
  'Bac + 3 ans', // Diplôme
  'Management des organisations', // Filière
  '330717L', // Code filière
  '1', // Niveau de formation (1-5)
  'Initiale', // Type de formation (Initiale ou Continue)
  'n', // Situation (n=Nouveau, r=Redoublant, p=Promu)
  'Baccalauréat', // Diplôme d'accès
  '', // Série du baccalauréat
  '2022/2023', // Année d'obtention du baccalauréat
  '', // Spécialité du diplôme d'accès
  'ab', // Mention (p/ab/b/tb/e/ns)
  'Province étrangère', // Lieu d'obtention
  '999', // Code Lieu d'obtention du Diplôme d'accès
  '2025/2026', // 1ère année d'inscription
  'n', // Handicapé (n/o)
  'n', // Résident (n/o)
  'n', // Fonctionnaire (n/o)
  '0%', // Pourcentage de la bourse
  'n', // Mobilité (n/o)
  '', // Documents fournis
  '2025/2026', // Session
  'En cours de vérification', // Statut
  '', // Motif de la décision
];

export const BulkImportStudentsModal: React.FC<BulkImportStudentsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BulkImportResponse | null>(null);

  // Options
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [academicYears, setAcademicYears] = useState<FilterOption[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string>('');
  // Applied to every row without a « Type de formation » column
  const [selectedTypeFormation, setSelectedTypeFormation] = useState<'INITIALE' | 'CONTINUE'>('INITIALE');
  const [includeInEdep, setIncludeInEdep] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load campuses and academic years when moving to options step
  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [campusesResponse, academicYearsResponse] = await Promise.all([
        fetchActiveCampuses(),
        getAcademicYears(),
      ]);

      setCampuses(campusesResponse.campuses);
      // Auto-select if only one campus
      if (campusesResponse.campuses.length === 1) {
        setSelectedCampusId(campusesResponse.campuses[0].id);
      }

      setAcademicYears(academicYearsResponse);
      // Auto-select current academic year
      const currentYear = academicYearsResponse.find(ay => ay.isCurrent);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.value);
      } else if (academicYearsResponse.length > 0) {
        // Fallback to first year if no current year is set
        setSelectedAcademicYearId(academicYearsResponse[0].value);
      }
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  // Download Excel template
  const handleDownloadTemplate = useCallback(() => {
    // Create CSV content (Excel-compatible)
    const csvContent = [
      TEMPLATE_HEADERS.join(';'),
      TEMPLATE_EXAMPLE_ROW.join(';'),
    ].join('\n');

    // Create blob with BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_import_etudiants.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleFileSelect = (selectedFile: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext =>
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
      setError('Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setStep('options');
    loadOptions();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setStep('importing');
    setError(null);

    try {
      const options: BulkImportOptions = {
        includeInEdep,
      };

      if (selectedCampusId) {
        options.defaultCampusId = selectedCampusId;
      }

      if (selectedAcademicYearId) {
        options.academicYearId = selectedAcademicYearId;
      }
      options.defaultTypeFormation = selectedTypeFormation;

      const response = await bulkImportStudents(file, options);

      // Set the results and transition to results step
      setResults(response);
      setStep('results');
    } catch (err) {
      console.error('[BulkImport] Error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
      setStep('options');
    }
  };

  const handleClose = () => {
    // If there were successful imports, notify parent to refresh the list
    const hadSuccessfulImports = results && results.successCount > 0;

    // Reset state
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
    setSelectedCampusId('');
    setSelectedAcademicYearId('');
    setIncludeInEdep(true); // Keep default as checked

    // Call onSuccess AFTER resetting state but BEFORE onClose
    if (hadSuccessfulImports) {
      onSuccess();
    }
    onClose();
  };

  const handleStartOver = () => {
    setStep('upload');
    setFile(null);
    setError(null);
    setResults(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 z-10">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-full">
              <FileSpreadsheet size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Import en masse des étudiants
              </h2>
              <p className="text-sm text-gray-500">
                Importez plusieurs étudiants depuis un fichier Excel
              </p>
            </div>
          </div>

          {/* Step: Upload */}
          {step === 'upload' && (
            <div>
              {/* Drop zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <Upload
                  size={48}
                  className={`mx-auto mb-4 ${
                    dragActive ? 'text-blue-500' : 'text-gray-400'
                  }`}
                />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Glissez-déposez votre fichier Excel ici
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  ou cliquez pour sélectionner un fichier
                </p>
                <p className="text-xs text-gray-400">
                  Formats acceptés : .xlsx, .xls, .csv (max 10 Mo)
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Error message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Help section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Format attendu</h4>
                  <button
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Download size={16} />
                    Télécharger le modèle
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Le fichier Excel doit contenir les colonnes suivantes (nomenclature du ministère) :
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><span className="font-medium text-red-600">* Nom</span> - Nom de famille (obligatoire)</li>
                  <li><span className="font-medium text-red-600">* Prénom</span> - Prénom (obligatoire)</li>
                  <li><span className="font-medium">Email</span> - Adresse email (optionnel)</li>
                  <li><span className="font-medium">N° Massar/CNE</span> - Numéro national étudiant</li>
                  <li><span className="font-medium">N° CNIE</span> - Carte nationale d'identité</li>
                  <li><span className="font-medium">Sexe</span> - m ou f</li>
                  <li><span className="font-medium">Date de naissance</span> - Format DD/MM/YYYY</li>
                  <li className="text-gray-400">+ autres champs optionnels...</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step: Options */}
          {step === 'options' && file && (
            <div>
              {/* Selected file */}
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <FileSpreadsheet size={24} className="text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">{file.name}</p>
                  <p className="text-sm text-green-700">
                    {(file.size / 1024).toFixed(1)} Ko
                  </p>
                </div>
                <button
                  onClick={handleStartOver}
                  className="text-green-600 hover:text-green-800"
                >
                  Changer
                </button>
              </div>

              {/* Options */}
              <div className="space-y-4 mb-6">
                {loadingOptions ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Chargement des options...</span>
                  </div>
                ) : (
                  <>
                    {/* Academic Year selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Année académique <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedAcademicYearId}
                        onChange={(e) => setSelectedAcademicYearId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">-- Sélectionner une année --</option>
                        {academicYears.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                            {year.isCurrent ? ' (En cours)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Les étudiants seront inscrits pour cette année académique
                      </p>
                    </div>

                    {/* Campus selection */}
                    {campuses.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Campus par défaut
                        </label>
                        <select
                          value={selectedCampusId}
                          onChange={(e) => setSelectedCampusId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">-- Sélectionner un campus --</option>
                          {campuses.map((campus) => (
                            <option key={campus.id} value={campus.id}>
                              {campus.name} - {campus.city}
                              {campus.isMainCampus ? ' (Principal)' : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Ce campus sera utilisé si non spécifié dans le fichier Excel
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Formation type: Initiale (default) or Continue for every row without the column */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de formation
                  </label>
                  <select
                    value={selectedTypeFormation}
                    onChange={(e) => setSelectedTypeFormation(e.target.value as 'INITIALE' | 'CONTINUE')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="INITIALE">Formation initiale</option>
                    <option value="CONTINUE">Formation continue</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Appliqué à tous les étudiants du fichier. Une colonne « Type de formation » (Initiale / Continue) dans le fichier
                    l'emporte ligne par ligne.
                  </p>
                </div>

                {/* E-DEP option */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeInEdep}
                    onChange={(e) => setIncludeInEdep(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Inclure dans la liste E-DEP</span>
                    <p className="text-xs text-gray-500">Les étudiants seront déclarés auprès du ministère</p>
                  </div>
                </label>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Retour
                </button>
                <button
                  onClick={handleImport}
                  disabled={!selectedAcademicYearId || (campuses.length > 1 && !selectedCampusId)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Upload size={18} />
                  Importer
                </button>
              </div>
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="py-12 text-center">
              <Loader2 size={48} className="mx-auto mb-4 text-blue-600 animate-spin" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Import en cours...
              </p>
              <p className="text-sm text-gray-500">
                Veuillez patienter, cela peut prendre quelques minutes
              </p>
            </div>
          )}

          {/* Step: Results */}
          {step === 'results' && results && (
            <div>
              {/* Success banner */}
              {results.successCount > 0 && (
                <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">
                        Import terminé avec succès !
                      </p>
                      <p className="text-sm text-green-700">
                        {results.successCount} étudiant{results.successCount > 1 ? 's' : ''} importé{results.successCount > 1 ? 's' : ''}.
                        Les emails de vérification ont été envoyés.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Success */}
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle size={24} className="mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-700">{results.successCount}</p>
                  <p className="text-sm text-green-600">Importés</p>
                </div>

                {/* Skipped */}
                <div className="p-4 bg-yellow-50 rounded-lg text-center">
                  <SkipForward size={24} className="mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold text-yellow-700">{results.skippedCount}</p>
                  <p className="text-sm text-yellow-600">Ignorés (doublons)</p>
                </div>

                {/* Errors */}
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <XCircle size={24} className="mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-700">{results.errorCount}</p>
                  <p className="text-sm text-red-600">Erreurs</p>
                </div>
              </div>

              {/* Skipped duplicates list */}
              {results.skippedDuplicates && results.skippedDuplicates.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} />
                    Doublons ignorés ({results.skippedDuplicates.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 rounded-lg p-3 text-sm">
                    {results.skippedDuplicates.map((dup, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b border-yellow-100 last:border-0">
                        <span className="text-yellow-900">
                          Ligne {dup.rowNumber}: {dup.prenom} {dup.nom}
                        </span>
                        <span className="text-yellow-700">{dup.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejected emails list - only shown if backend returns this field */}
              {results.rejectedMissingEmail && results.rejectedMissingEmail.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <Ban size={16} />
                    Rejetés (email manquant) ({results.rejectedMissingEmail.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.rejectedMissingEmail.map((rej, idx) => (
                      <div key={idx} className="py-1 border-b border-red-100 last:border-0 text-red-900">
                        Ligne {rej.rowNumber}: {rej.prenom} {rej.nom}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error list */}
              {results.results.filter(r => !r.success && r.error).length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Erreurs d'import
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 text-sm">
                    {results.results
                      .filter(r => !r.success && r.error)
                      .map((res, idx) => (
                        <div key={idx} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                          <span className="text-red-900">
                            Ligne {res.rowNumber}: {res.prenom} {res.nom}
                          </span>
                          <span className="text-red-700">{res.error}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Importer un autre fichier
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportStudentsModal;
